<template>
  <div class="p-6 bg-gradient-to-br from-blue-50 to-white w-full">
    <!-- Booking Search Section (copied from Home.vue) -->
    <div class="w-full max-w-5xl bg-white/90 rounded-2xl shadow-lg p-6 mb-10 mx-auto">
      <!-- Search Form: All in one line -->
      <form
        class="flex flex-col gap-3 md:flex-row md:gap-3 items-center w-full"
        @submit.prevent="onSearch"
      >
        <!-- City Selector -->
        <div class="flex-1 w-full min-w-[200px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-geo-alt"></i>
            </span>
            <input
              v-model="search.city"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
              placeholder="Enter city or location"
              required
              @input="searchCities"
              @focus="searchCities"
              @blur="handleCityBlur"
              autocomplete="off"
            />
            <div
              v-if="showCityDropdown && cities.length"
              class="absolute z-10 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto"
            >
              <div v-if="cityLoading" class="p-2 text-gray-500 text-center">
                Loading...
              </div>
              <div v-else>
                <div
                  v-for="city in cities"
                  :key="city.id"
                  @mousedown.prevent="selectCity(city)"
                  class="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                >
                  {{ city.name }}
                  <span v-if="city.state">, {{ city.state.name }}</span>
                  <span v-if="city.state && city.state.country"
                    >, {{ city.state.country.name }}</span
                  >
                </div>
              </div>
            </div>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Date Picker -->
        <div class="flex-1 w-full min-w-[250px] md:w-auto">
          <div class="relative" data-input>
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-calendar-date"></i>
            </span>
            <flat-pickr
              v-model="search.dateRange"
              :config="dateOptions"
              class="pl-12 pr-6 w-full border border-gray-200 rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
              placeholder="Select check-in and check-out"
              :readonly="true"
            >
              <input
                type="text"
                data-input
                readonly
                class="pl-12 pr-6 w-full border border-gray-200 rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
                placeholder="Select check-in and check-out"
              />
            </flat-pickr>
          </div>
        </div>
        <!-- Adults -->
        <div class="flex-shrink-0 w-full md:w-auto min-w-[120px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-people"></i>
            </span>
            <select
              v-model="search.adults"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
              required
            >
              <option disabled value="">Adult</option>
              <option v-for="n in 5" :key="n" :value="n">
                {{ n }} Adult{{ n > 1 ? "s" : "" }}
              </option>
            </select>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Children -->
        <div class="flex-shrink-0 w-full md:w-auto min-w-[120px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-person-badge"></i>
            </span>
            <select
              v-model="search.children"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
            >
              <option disabled value="">Child</option>
              <option v-for="n in 5" :key="n" :value="n">
                {{ n }} Child{{ n > 1 ? "ren" : "" }}
              </option>
            </select>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Search Button -->
        <div class="flex-shrink-0 w-full md:w-auto">
          <button
            type="submit"
            class="h-12 w-full md:w-auto bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg px-6 font-bold shadow-lg transition flex items-center justify-center"
          >
            <i class="bi bi-search mr-2"></i>Search
          </button>
        </div>
      </form>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <div v-else-if="error" class="text-center py-8 text-red-500">
      <i class="bi bi-exclamation-triangle text-4xl mb-4"></i>
      <p>{{ error }}</p>
    </div>

    <div v-else-if="hotel" class="max-w-6xl mx-auto">
      <!-- Hotel Header -->
      <div class="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div class="flex flex-col md:flex-row gap-6">
          <!-- Hotel Images Gallery -->
          <div class="md:w-1/2">
            <div v-if="hotel.galleries && hotel.galleries.length > 0" class="space-y-4">
              <!-- Bootstrap Carousel -->
              <div
                id="hotelGalleryCarousel"
                class="carousel slide"
                data-bs-ride="carousel"
              >
                <div class="carousel-inner">
                  <div
                    v-for="(gallery, index) in hotel.galleries"
                    :key="gallery.id"
                    :class="['carousel-item', { active: index === 0 }]"
                  >
                    <img
                      :src="gallery.path"
                      :alt="gallery.name"
                      class="d-block w-100 h-80 object-cover rounded-lg shadow-md"
                    />
                  </div>
                </div>

                <!-- Controls -->
                <button
                  class="carousel-control-prev"
                  type="button"
                  data-bs-target="#hotelGalleryCarousel"
                  data-bs-slide="prev"
                >
                  <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Previous</span>
                </button>
                <button
                  class="carousel-control-next"
                  type="button"
                  data-bs-target="#hotelGalleryCarousel"
                  data-bs-slide="next"
                >
                  <span class="carousel-control-next-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Next</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Hotel Info -->
          <div class="md:w-1/2 space-y-4">
            <div>
              <h1 class="text-3xl font-bold text-gray-800 mb-2">{{ hotel.name }}</h1>
              <div class="flex items-center mb-2">
                <i class="bi bi-star-fill text-yellow-400 mr-1"></i>
                <span class="text-lg font-medium">{{ hotel.rating }}</span>
                <span class="text-gray-500 ml-2">({{ hotel.rating }} out of 5)</span>
              </div>
            </div>

            <div class="space-y-3">
              <div class="flex items-center text-gray-600">
                <i class="bi bi-geo-alt mr-2 text-blue-500"></i>
                <span
                  >{{ hotel.address }}, {{ hotel.city?.name }},
                  {{ hotel.country?.name }}</span
                >
              </div>

              <div class="flex items-center text-gray-600">
                <i class="bi bi-envelope mr-2 text-blue-500"></i>
                <span>{{ hotel.contact_email }}</span>
              </div>

              <div class="flex items-center text-gray-600">
                <i class="bi bi-telephone mr-2 text-blue-500"></i>
                <span>{{ hotel.phone }}</span>
              </div>

              <div class="flex items-center text-gray-600">
                <i class="bi bi-clock mr-2 text-blue-500"></i>
                <span
                  >Check-in: {{ hotel.check_in_time }} | Check-out:
                  {{ hotel.check_out_time }}</span
                >
              </div>
            </div>

            <div class="pt-4">
              <p class="text-gray-700 leading-relaxed">{{ hotel.description }}</p>
            </div>

            <!-- Book Now Button -->
            <div class="pt-4">
              <button
                @click="bookNow"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center"
                v-if="rooms.length > 0"
              >
                <i class="bi bi-calendar-check mr-2"></i>
                Book Now
              </button>
              <button
                class="w-full bg-gray-500 text-white py-3 px-6 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center"
                v-else
              >
                Currently No Room Available
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Hotel Group Info -->
      <div v-if="hotel.hotel_group" class="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 class="text-xl font-semibold mb-3">Hotel Group</h3>
        <div class="space-y-2">
          <p><strong>Name:</strong> {{ hotel.hotel_group.name }}</p>
          <p><strong>Description:</strong> {{ hotel.hotel_group.description }}</p>
        </div>
      </div>

      <!-- Available Rooms -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <h3 class="text-xl font-semibold mb-4">Available Room Types</h3>

        <div v-if="rooms.length === 0" class="text-center py-8 text-gray-500">
          <i class="bi bi-door-open text-4xl mb-4"></i>
          <p>No room types available at the moment.</p>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="room in rooms"
            :key="room.id"
            class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            <!-- Room Image Carousel -->
            <div v-if="room.attachments && room.attachments.length" class="h-48">
              <div
                :id="'roomCarousel-' + room.id"
                class="carousel slide h-full"
                data-bs-ride="carousel"
              >
                <div class="carousel-inner h-full">
                  <div
                    v-for="(attachment, index) in room.attachments"
                    :key="attachment.id"
                    :class="['carousel-item h-full', { active: index === 0 }]"
                  >
                    <img
                      :src="attachment.path"
                      :alt="attachment.name"
                      class="d-block w-100 h-full object-cover"
                    />
                  </div>
                </div>

                <!-- Controls -->
                <button
                  class="carousel-control-prev"
                  type="button"
                  :data-bs-target="'#roomCarousel-' + room.id"
                  data-bs-slide="prev"
                >
                  <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Previous</span>
                </button>
                <button
                  class="carousel-control-next"
                  type="button"
                  :data-bs-target="'#roomCarousel-' + room.id"
                  data-bs-slide="next"
                >
                  <span class="carousel-control-next-icon" aria-hidden="true"></span>
                  <span class="visually-hidden">Next</span>
                </button>
              </div>
            </div>
            <div
              v-else
              class="h-48 bg-gray-100 flex items-center justify-center text-gray-400"
            >
              <i class="bi bi-image text-4xl"></i>
            </div>

            <!-- Room Info -->
            <div class="p-4">
              <h4 class="font-semibold text-lg mb-1">{{ room.name }}</h4>
              <p class="text-sm text-gray-500 mb-3">Hotel: {{ room.hotel?.name }}</p>
              <p class="text-gray-600 mb-2">
                {{ room.description || "No description available" }}
              </p>

              <div class="space-y-2 mb-3">
                <div class="flex items-center text-sm text-gray-600">
                  <i class="bi bi-bed mr-2 text-blue-500"></i>
                  <span>{{ room.bed_type }}</span>
                </div>
                <div class="flex items-center text-sm text-gray-600">
                  <i class="bi bi-people mr-2 text-blue-500"></i>
                  <span>Capacity: {{ room.capacity }} persons</span>
                </div>
                <div
                  v-if="room.size_sqft"
                  class="flex items-center text-sm text-gray-600"
                >
                  <i class="bi bi-arrows-angle-expand mr-2 text-blue-500"></i>
                  <span>{{ room.size_sqft }} sq ft</span>
                </div>
                <div v-if="room.view" class="flex items-center text-sm text-gray-600">
                  <i class="bi bi-eye mr-2 text-blue-500"></i>
                  <span>{{ room.view }}</span>
                </div>
                <div
                  v-if="room.amenities && room.amenities.length"
                  class="flex flex-wrap gap-2 text-sm text-gray-600"
                >
                  <span
                    v-for="am in room.amenities"
                    :key="am.id"
                    class="bg-blue-50 text-blue-600 px-2 py-1 rounded"
                  >
                    {{ am.facility?.name }}
                  </span>
                </div>
                <div class="flex items-center text-sm text-gray-600">
                  <i class="bi bi-check-circle mr-2 text-green-500"></i>
                  <span>Available Rooms: {{ room.available_rooms }}</span>
                </div>
              </div>

              <div class="border-t pt-3">
                <p class="text-green-600 font-semibold text-lg">
                  ₹{{ parseFloat(room.base_price).toFixed(2) }}
                </p>
                <p class="text-xs text-gray-500">per night</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gallery Modal using BootstrapVue -->
      <b-modal
        v-model="showGalleryModal"
        size="xl"
        hide-footer
        no-backdrop
        title="Gallery"
        dialog-class="p-0"
        content-class="p-0"
        body-class="p-0"
      >
        <div class="container-fluid p-4">
          <div class="row">
            <div
              v-for="gallery in hotel.galleries"
              :key="gallery.id"
              class="col-6 col-md-4 col-lg-3 mb-4"
            >
              <img
                :src="gallery.path"
                :alt="gallery.name"
                class="w-100 rounded shadow-sm"
                style="height: 200px; object-fit: cover"
              />
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "UserHotelDetails",
  components: { FlatPickr },
  data() {
    return {
      hotel: null,
      rooms: [],
      loading: true,
      error: null,
      mainImage: null,
      showGalleryModal: false,
      search: {
        city: this.$route.query.city || "",
        dateRange: [this.$route.query.check_in || "", this.$route.query.check_out || ""],
        adults: this.$route.query.adults || "",
        children: this.$route.query.children || "",
      },
      showCityDropdown: false,
      cityLoading: false,
      cities: [],
      selectedCity: null,
      dateOptions: {
        mode: "range",
        dateFormat: "d M Y",
        altInput: true,
        altFormat: "d M Y",
        allowInput: true,
        showMonths: 2,
        minDate: "today",
        wrap: true,
        onReady(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
        onOpen(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
      },
    };
  },
  async created() {
    const id = this.$route.params.id;
    await this.fetchHotelDetails(id);
    await this.fetchAvailableRoomTypes(id);
  },
  methods: {
    openGalleryModal() {
      this.showGalleryModal = true;
    },
    async fetchHotelDetails(id) {
      try {
        const response = await request({ method: "get", url: `/public/hotels/${id}` });
        this.hotel = response.data;
        if (this.hotel.galleries && this.hotel.galleries.length > 0) {
          this.mainImage = this.hotel.galleries[0].path;
        }
      } catch (error) {
        this.error = "Failed to load hotel details. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async fetchAvailableRoomTypes(id) {
      try {
        const [check_in, check_out] = Array.isArray(this.search.dateRange)
          ? this.search.dateRange
          : ["", ""];
        const params = {
          hotel_id: id,
          check_in,
          check_out,
          adults: this.search.adults,
          children: this.search.children,
        };
        const response = await request({
          method: "get",
          url: "/public/available-room-types",
          params,
        });
        this.rooms =
          response.data.room_types || response.data.data || response.data || [];
      } catch (error) {
        this.rooms = [];
      }
    },
    setMainImage(imagePath) {
      this.mainImage = imagePath;
    },
    bookNow() {
      const [check_in, check_out] = Array.isArray(this.search.dateRange) ? this.search.dateRange : ['', ''];
      
      const formatDateToYYYYMMDD = (dateString) => {
        if (!dateString) return "";

        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
      };

      const formattedCheckIn = formatDateToYYYYMMDD(check_in);
      const formattedCheckOut = formatDateToYYYYMMDD(check_out);

      // console.log("Original dates:", { check_in, check_out });
      // console.log("Formatted dates:", { formattedCheckIn, formattedCheckOut });
      if (this.rooms.length) {
        this.$router.push({
          path: "/booking",
          query: {
            hotel_id: this.hotel.id,
            hotel_name: this.hotel.name,
            check_in: formattedCheckIn,
            check_out: formattedCheckOut,
            adults: this.search.adults,
            children: this.search.children,
          },
        }).catch(() => {}); 
      }
    },
    async onSearch() {
      let checkin = "";
      let checkout = "";
      if (
        typeof this.search.dateRange === "string" &&
        this.search.dateRange.includes(" to ")
      ) {
        [checkin, checkout] = this.search.dateRange.split(" to ");
      } else if (
        Array.isArray(this.search.dateRange) &&
        this.search.dateRange.length === 2
      ) {
        [checkin, checkout] = this.search.dateRange;
      }

      let cityId = this.selectedCity?.id || this.$route.query.cityId || "";
      this.$router.push({
        path: "/hotels",
        query: {
          city: this.search.city,
          cityId: cityId,
          check_in: checkin,
          check_out: checkout,
          adults: this.search.adults,
          children: this.search.children,
        },
      });
    },
    async searchCities() {
      const query = this.search.city;
      if (query.length >= 2) {
        this.cityLoading = true;
        try {
          const response = await request({
            method: "get",
            url: `/cities?search=${encodeURIComponent(query)}`,
          });
          if (response.data) {
            this.cities = response.data;
            this.showCityDropdown = true;
          } else {
            this.cities = [];
            this.showCityDropdown = false;
          }
        } catch (error) {
          this.cities = [];
          this.showCityDropdown = false;
        } finally {
          this.cityLoading = false;
        }
      } else {
        this.cities = [];
        this.showCityDropdown = false;
      }
    },
    handleCityBlur() {
      setTimeout(() => {
        this.showCityDropdown = false;
      }, 200);
    },
    selectCity(cityObj) {
      this.search.city = cityObj.name;
      this.selectedCity = cityObj;
      this.showCityDropdown = false;
      this.cities = [];
    },
  },
  watch: {
    "$route.query": {
      handler() {
        this.search.city = this.$route.query.city || "";
        this.search.dateRange = [
          this.$route.query.check_in || "",
          this.$route.query.check_out || "",
        ];
        this.search.adults = this.$route.query.adults || "";
        this.search.children = this.$route.query.children || "";
        if (this.hotel && this.hotel.id) {
          this.fetchAvailableRoomTypes(this.hotel.id);
        }
      },
      immediate: false,
    },
  },
};
</script>
